import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import {
  Building2, CreditCard, Palette as PaletteIcon, Sliders,
  Upload, X as XIcon,
} from "lucide-react";
import SubscriptionTab from "./SubscriptionTab";

// Feature labels mirror TENANT_FEATURE_REGISTRY on the backend. Kept as
// a small hardcoded map so the settings tab doesn't need an extra fetch
// — the platform admin controls the list, so it changes rarely.
const FEATURE_LABELS = {
  "food-prep":            "Food Prep",
  "training-coms":        "Training & Coms",
  "cooking-record":       "Cooking Record",
  "hot-cold-hold":        "Hot & Cold Hold",
  "thawing":              "Thawing",
  "metal-detector":       "Metal Detector",
  "visitor-registration": "Visitor Registration",
  "forms":                "Forms",
  "pest-control":         "Pest Control",
  "approved-vendor":      "Approved Vendor",
  "compliance-documents": "Compliance Documents",
};

/**
 * Read-only chips showing which of the 10 tenant features are currently
 * enabled for this workspace. Renders under Company Profile so owners
 * can see what's on without asking support.
 */
function EnabledFeaturesPanel() {
  const { effectiveFeatures } = useAuth();
  const keys = Object.keys(FEATURE_LABELS);
  const enabled = keys.filter((k) => effectiveFeatures?.[k] !== false);
  return (
    <div data-testid="settings-enabled-features">
      <label className="text-xs uppercase font-semibold opacity-70">
        Enabled features
      </label>
      <p className="text-xs opacity-60 mt-1 mb-2">
        Controlled by your platform administrator. Contact support to
        enable a feature that is currently off.
      </p>
      {enabled.length === 0 ? (
        <p className="text-xs opacity-60 italic">
          No features are currently enabled for this workspace.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {enabled.map((k) => (
            <span key={k} className="vb-pill ok"
              data-testid={`settings-feature-${k}`}>
              {FEATURE_LABELS[k]}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workspace Settings — 4 tabs (Company profile / Business / Brand theme /
// Subscription). The platform admin alone curates the 3 themes; tenants only
// pick from the active list. Brand-theme tab is owner-only.
// ---------------------------------------------------------------------------
const TABS = [
  ["profile",      "Company Profile",   Building2],
  ["business",     "Business Settings", Sliders],
  ["theme",        "Brand Theme",       PaletteIcon],
  ["subscription", "Subscription",      CreditCard],
];

const PALETTE_TO_VAR = {
  primary_bg:     "--vb-bg",
  secondary_bg:   "--vb-bg-2",
  primary_text:   "--vb-text",
  secondary_text: "--vb-text-2",
  accent:         "--vb-accent",
  warning:        "--vb-warning",
  error:          "--vb-error",
};

function applyPaletteLive(palette) {
  const root = document.documentElement.style;
  Object.entries(PALETTE_TO_VAR).forEach(([k, v]) => {
    if (palette?.[k]) root.setProperty(v, palette[k]);
  });
}

const TIMEZONES = [
  "UTC",
  "Asia/Singapore", "Asia/Kuala_Lumpur", "Asia/Hong_Kong", "Asia/Tokyo",
  "Asia/Shanghai", "Asia/Kolkata", "Australia/Sydney",
  "Europe/London", "Europe/Paris",
  "America/New_York", "America/Los_Angeles",
];

export default function Settings() {
  const { user, tenant, refresh } = useAuth();
  const dialog = useDialog();
  const isOwner = user?.role === "owner";

  const visibleTabs = TABS.filter(([id]) => {
    // Subscription is owner-only — managers/supervisors don't see billing.
    if (id === "subscription" && !isOwner) return false;
    // Brand-theme is owner-only too (it changes the look for the whole tenant).
    if (id === "theme" && !isOwner) return false;
    return true;
  });
  const [tab, setTab] = useState(visibleTabs[0]?.[0] || "profile");

  // Deep-link support (?tab=subscription used by cap-alert "Upgrade" links).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t && visibleTabs.find(([id]) => id === t)) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------- Company profile ----------------
  const [profile, setProfile] = useState({
    legal_company_name: "", tagline: "", address: "",
    company_email: "", company_phone: "", uen_number: "",
  });
  const [logoUrl, setLogoUrl] = useState("");
  const logoInputRef = useRef(null);

  // ---------------- Business / operational ----------------
  const [op, setOp] = useState({ timezone: "UTC", date_format: "DD/MM/YYYY", temperature_unit: "C" });

  // ---------------- Brand theme ----------------
  const [themes, setThemes] = useState([]);
  const [currentThemeId, setCurrentThemeId] = useState(null);
  const [previewThemeId, setPreviewThemeId] = useState(null);

  const [tierName, setTierName] = useState("");

  // Per-form busy + message
  const [busyProfile, setBusyProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ ok: "", err: "" });
  const [busyBiz, setBusyBiz] = useState(false);
  const [bizMsg, setBizMsg] = useState({ ok: "", err: "" });
  const [busyTheme, setBusyTheme] = useState(false);
  const [themeMsg, setThemeMsg] = useState({ ok: "", err: "" });

  const load = async () => {
    try {
      const [{ data: prof }, { data: opData }, { data: feat }] = await Promise.all([
        api.get("/tenant/profile"),
        api.get("/tenant/operational"),
        api.get("/tenant/features"),
      ]);
      const p = prof.profile || {};
      setProfile({
        legal_company_name: p.legal_company_name || prof.company_name || "",
        tagline: p.tagline || "",
        address: p.address || "",
        company_email: p.company_email || "",
        company_phone: p.company_phone || "",
        uen_number: p.uen_number || "",
      });
      setLogoUrl(tenant?.branding?.logo_url || "");
      setOp(opData.operational || {});
      setTierName(feat.tier_name || "");
    } catch (e) { console.debug("Settings load failed:", e); }

    // Themes (separate so a 401 here doesn't kill the rest)
    try {
      const { data: th } = await api.get("/themes");
      setThemes(th.themes || []);
      const tid = tenant?.theme_id || null;
      setCurrentThemeId(tid);
      setPreviewThemeId(tid);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenant?.id]);

  // -------------- profile actions --------------
  const saveProfile = async (e) => {
    e.preventDefault();
    setBusyProfile(true); setProfileMsg({ ok: "", err: "" });
    try {
      await api.put("/tenant/profile", profile);
      await refresh();
      setProfileMsg({ ok: "Company profile saved.", err: "" });
    } catch (ex) { setProfileMsg({ ok: "", err: formatErr(ex.response?.data?.detail) }); }
    finally { setBusyProfile(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusyProfile(true); setProfileMsg({ ok: "", err: "" });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tenant/branding/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.url);
      await refresh();
      setProfileMsg({ ok: "Logo uploaded.", err: "" });
    } catch (ex) { setProfileMsg({ ok: "", err: formatErr(ex.response?.data?.detail) }); }
    finally {
      setBusyProfile(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    const ok = await dialog.confirm({
      title: "Remove logo?",
      message: "The default Vanguard Beacon sidebar logo will be shown until you upload a new one.",
      danger: true, confirmLabel: "Remove",
    });
    if (!ok) return;
    try {
      // Re-use branding update — clear the logo_url field.
      const updated = { ...(tenant?.branding || {}), logo_url: "" };
      await api.put("/tenant/branding", updated);
      setLogoUrl("");
      await refresh();
      setProfileMsg({ ok: "Logo removed.", err: "" });
    } catch (ex) { setProfileMsg({ ok: "", err: formatErr(ex.response?.data?.detail) }); }
  };

  // -------------- business actions --------------
  const saveBiz = async (e) => {
    e.preventDefault();
    setBusyBiz(true); setBizMsg({ ok: "", err: "" });
    try {
      await api.put("/tenant/operational", op);
      setBizMsg({ ok: "Business settings saved.", err: "" });
    } catch (ex) { setBizMsg({ ok: "", err: formatErr(ex.response?.data?.detail) }); }
    finally { setBusyBiz(false); }
  };

  // -------------- theme actions --------------
  const previewTheme = (theme) => {
    setPreviewThemeId(theme.id);
    applyPaletteLive(theme.palette);
  };
  const cancelPreview = () => {
    setPreviewThemeId(currentThemeId);
    const current = themes.find((t) => t.id === currentThemeId);
    if (current) applyPaletteLive(current.palette);
    else window.location.reload();
  };
  const saveTheme = async () => {
    if (!previewThemeId || previewThemeId === currentThemeId) return;
    setBusyTheme(true); setThemeMsg({ ok: "", err: "" });
    try {
      await api.put("/tenant/theme", { theme_id: previewThemeId });
      const picked = themes.find((t) => t.id === previewThemeId);
      setCurrentThemeId(previewThemeId);
      setThemeMsg({ ok: `Theme "${picked?.name}" applied across your workspace.`, err: "" });
      await refresh();
    } catch (ex) { setThemeMsg({ ok: "", err: formatErr(ex.response?.data?.detail) }); }
    finally { setBusyTheme(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl vb-fade-in" data-testid="settings-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="vb-heading text-3xl m-0" style={{ color: "var(--vb-text-2)" }}>Workspace Settings</h1>
          <p className="opacity-70 text-sm mt-1 mb-0">Manage your company profile, business settings, brand colors and subscription.</p>
        </div>
        {tierName && (
          <div className="vb-pill ok inline-flex items-center gap-1.5" data-testid="settings-plan-pill">
            Current plan: <strong>{tierName}</strong>
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="vb-tabs" data-testid="settings-tabs">
        {visibleTabs.map(([id, label, Icon]) => (
          <button
            key={id} type="button"
            onClick={() => setTab(id)}
            className={`vb-tab ${tab === id ? "active" : ""}`}
            data-testid={`settings-tab-${id}`}
          >
            <Icon size={14} className="inline mr-1.5 -mt-0.5" />
            {label}
          </button>
        ))}
      </div>

      {/* ========== Tab 1: COMPANY PROFILE ========== */}
      {tab === "profile" && (
        <form className="vb-card p-7 space-y-4" onSubmit={saveProfile} data-testid="settings-profile-card">
          <div className="flex items-center gap-2">
            <Building2 size={20} style={{ color: "var(--vb-text-2)" }} />
            <h2 className="vb-heading text-2xl m-0" style={{ color: "var(--vb-text-2)" }}>Company profile</h2>
          </div>

          {profileMsg.err && <div className="vb-pill err w-full" data-testid="profile-err">{profileMsg.err}</div>}
          {profileMsg.ok && <div className="vb-pill ok w-full" data-testid="profile-ok">{profileMsg.ok}</div>}

          <div>
            <Label>Company logo (200×200 recommended)</Label>
            <div className="flex items-center gap-4" data-testid="set-logo-row">
              <div
                style={{
                  width: 96, height: 96, borderRadius: 8,
                  border: "1px dashed var(--vb-border)",
                  background: "var(--vb-bg-2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  overflow: "hidden", flexShrink: 0,
                }}
              >
                {logoUrl
                  ? <img src={logoUrl} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} data-testid="set-logo-preview" />
                  : <span className="text-xs opacity-50">No logo</span>}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={logoInputRef} type="file"
                  accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
                  style={{ display: "none" }}
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                  disabled={!isOwner}
                  data-testid="set-logo-input"
                />
                <button
                  type="button" className="vb-btn ghost sm"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={busyProfile || !isOwner}
                  data-testid="set-logo-upload"
                >
                  <Upload size={13} /> {logoUrl ? "Replace logo" : "Upload logo"}
                </button>
                {logoUrl && isOwner && (
                  <button
                    type="button" className="vb-btn ghost sm"
                    onClick={removeLogo} disabled={busyProfile}
                    style={{ color: "var(--vb-error)" }}
                    data-testid="set-logo-remove"
                  >
                    <XIcon size={13} /> Remove
                  </button>
                )}
                <p className="text-xs opacity-60">PNG, JPG, or SVG · max 4 MB</p>
              </div>
            </div>
          </div>

          <div>
            <Label>Legal company name</Label>
            <input className="vb-input" disabled={!isOwner}
              value={profile.legal_company_name}
              onChange={(e) => setProfile({ ...profile, legal_company_name: e.target.value })}
              data-testid="set-company" />
          </div>
          <div>
            <Label>Tagline</Label>
            <input className="vb-input" disabled={!isOwner}
              value={profile.tagline}
              onChange={(e) => setProfile({ ...profile, tagline: e.target.value })}
              placeholder="Short one-liner shown on reports and emails"
              data-testid="set-tagline" />
          </div>
          <div>
            <Label>Business address</Label>
            <textarea className="vb-input" rows={2} disabled={!isOwner}
              value={profile.address}
              onChange={(e) => setProfile({ ...profile, address: e.target.value })}
              data-testid="set-address" />
          </div>
          <EnabledFeaturesPanel />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Contact email</Label>
              <input className="vb-input" type="email" disabled={!isOwner}
                value={profile.company_email}
                onChange={(e) => setProfile({ ...profile, company_email: e.target.value })}
                data-testid="set-email" />
            </div>
            <div>
              <Label>Contact phone</Label>
              <input className="vb-input" disabled={!isOwner}
                value={profile.company_phone}
                onChange={(e) => setProfile({ ...profile, company_phone: e.target.value })}
                data-testid="set-phone" />
            </div>
          </div>

          {isOwner && (
            <button className="vb-btn w-full" disabled={busyProfile} data-testid="set-save-profile">
              {busyProfile ? "Saving…" : "Save company profile"}
            </button>
          )}
        </form>
      )}

      {/* ========== Tab 2: BUSINESS SETTINGS ========== */}
      {tab === "business" && (
        <form className="vb-card p-7 space-y-5" onSubmit={saveBiz} data-testid="settings-business-card">
          <div className="flex items-center gap-2">
            <Sliders size={20} style={{ color: "var(--vb-text-2)" }} />
            <h2 className="vb-heading text-2xl m-0" style={{ color: "var(--vb-text-2)" }}>Business settings</h2>
          </div>
          {bizMsg.err && <div className="vb-pill err w-full" data-testid="biz-err">{bizMsg.err}</div>}
          {bizMsg.ok && <div className="vb-pill ok w-full" data-testid="biz-ok">{bizMsg.ok}</div>}

          <h3 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Operational</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Business timezone</Label>
              <select className="vb-input" value={op.timezone} disabled={!isOwner}
                onChange={(e) => setOp({ ...op, timezone: e.target.value })}
                data-testid="set-timezone">
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz.replace("_", " ")}</option>)}
              </select>
              <p className="text-xs opacity-60 mt-1">Drives the end-of-day cut-off for daily Hygiene / Cleanliness logs and report timestamps.</p>
            </div>
            <div>
              <Label>Date format</Label>
              <select className="vb-input" value={op.date_format} disabled={!isOwner}
                onChange={(e) => setOp({ ...op, date_format: e.target.value })}
                data-testid="set-date-format">
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
              <p className="text-xs opacity-60 mt-1">Used across the app and on generated PDF reports.</p>
            </div>
            <div>
              <Label>Default temperature unit</Label>
              <select className="vb-input" value={op.temperature_unit} disabled={!isOwner}
                onChange={(e) => setOp({ ...op, temperature_unit: e.target.value })}
                data-testid="set-temp-unit">
                <option value="C">°C (Celsius)</option>
                <option value="F">°F (Fahrenheit)</option>
              </select>
              <p className="text-xs opacity-60 mt-1">CCP readings logged in this unit by default. Staff can still override per reading.</p>
            </div>
          </div>

          {isOwner && (
            <button className="vb-btn w-full" disabled={busyBiz} data-testid="set-save-business">
              {busyBiz ? "Saving…" : "Save business settings"}
            </button>
          )}
        </form>
      )}

      {/* ========== Tab 3: BRAND THEME (owner-only) ========== */}
      {tab === "theme" && isOwner && (
        <div className="vb-card p-7" data-testid="set-theme-card">
          <div className="flex items-center gap-2 mb-1">
            <PaletteIcon size={20} style={{ color: "var(--vb-text-2)" }} />
            <h2 className="vb-heading text-2xl m-0" style={{ color: "var(--vb-text-2)" }}>Brand theme</h2>
          </div>
          <p className="text-sm opacity-70 mb-6">
            Pick a curated theme for your workspace. Click a card to preview the look live across the app — then hit Save to apply it permanently.
          </p>

          {themeMsg.err && <div className="vb-pill err mb-4 w-full" data-testid="theme-err">{themeMsg.err}</div>}
          {themeMsg.ok && <div className="vb-pill ok mb-4 w-full" data-testid="theme-ok">{themeMsg.ok}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-testid="theme-cards">
            {themes.map((th) => (
              <ThemeCard
                key={th.id}
                theme={th}
                isSelected={previewThemeId === th.id}
                isSaved={currentThemeId === th.id}
                onClick={() => previewTheme(th)}
              />
            ))}
            {themes.length === 0 && (
              <div className="text-sm opacity-60 col-span-full">No themes available. Please contact your administrator.</div>
            )}
          </div>

          <div className="flex gap-3">
            <button type="button" className="vb-btn flex-1"
              disabled={busyTheme || !previewThemeId || previewThemeId === currentThemeId}
              onClick={saveTheme}
              data-testid="set-save-theme">
              {busyTheme ? "Saving…" : "Save theme"}
            </button>
            {previewThemeId !== currentThemeId && (
              <button type="button" className="vb-btn ghost" onClick={cancelPreview}
                disabled={busyTheme} data-testid="set-cancel-preview">
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* ========== Tab 4: SUBSCRIPTION (owner-only) ========== */}
      {tab === "subscription" && isOwner && <SubscriptionTab />}
    </div>
  );
}


function ThemeCard({ theme, isSelected, isSaved, onClick }) {
  const p = theme.palette || {};
  return (
    <button
      type="button" onClick={onClick}
      className="vb-card p-0 text-left overflow-hidden"
      style={{
        cursor: "pointer",
        borderWidth: 2,
        borderColor: isSelected ? "var(--vb-accent)" : "var(--vb-border)",
        transition: "transform 120ms ease, border-color 120ms ease",
        transform: isSelected ? "translateY(-2px)" : "none",
      }}
      data-testid={`theme-card-${theme.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex" style={{ height: 56 }}>
        <div style={{ flex: 2, background: p.primary_bg }} />
        <div style={{ flex: 1, background: p.secondary_bg }} />
        <div style={{ flex: 1, background: p.accent }} />
        <div style={{ flex: 1, background: p.warning }} />
        <div style={{ flex: 1, background: p.error }} />
      </div>
      <div style={{ background: p.primary_bg, padding: "12px 14px" }}>
        <div className="vb-heading text-lg" style={{ color: p.secondary_text, marginBottom: 4 }}>{theme.name}</div>
        <div className="text-xs" style={{ color: p.primary_text, lineHeight: 1.4, minHeight: 60 }}>{theme.description}</div>
      </div>
      <div className="flex items-center justify-between px-3 py-2" style={{ borderTop: "1px solid var(--vb-border)", background: "var(--vb-bg-2)" }}>
        <div className="flex gap-1 flex-wrap">
          {isSaved && <span className="vb-pill ok text-[10px]">Current</span>}
          {theme.is_default && <span className="vb-pill text-[10px]">Default</span>}
        </div>
        {isSelected && !isSaved && <span className="text-[10px] font-semibold" style={{ color: "var(--vb-accent)" }}>PREVIEWING</span>}
      </div>
    </button>
  );
}

function Label({ children }) {
  return (
    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>
      {children}
    </label>
  );
}
